import { type FC, type KeyboardEvent } from "react";

import { clsx } from "clsx";

import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * `.choice-card` per design handoff Part 2 — the smaller picker variant.
 *
 * Used by:
 *   - B0.5 / B1 (AddRecordsClassifyOverlay step 1) — Supplemental vs Follow-Up
 *   - Design handoff C1 / PL-4 (RecordsUploadZone followup case) — Original Medical Records vs Prior Review Document
 *
 * Distinct from `.eval-choice` (the larger A1 variant in process_records_card)
 * which has 24px padding, 32px icon, 16px title — see Part 2 for the size diff.
 *
 * Spec:
 *   border:        2px solid slate-200
 *   border-radius: 10px
 *   padding:       20px
 *   transition:    all 0.15s
 *   position:      relative
 *
 * .cc-radio:       absolute top:14 right:14, 20×20, 2px slate-300 default
 * .cc-icon:        28px, mb 12px (mb 22px when withRecommended modifier)
 * .cc-title:       15px / 700 / slate-900
 * .cc-desc:        13px / 400 / slate-500
 * .cc-billing:     12px / 500 / indigo-500, mt 10px
 * .cc-recommended: absolute top:14 LEFT:14, 10px/700/uppercase/0.04em letter-
 *                  spacing, indigo-500 text on indigo-100 bg, 2×7 padding,
 *                  4px radius. Pushes .cc-icon down 22px so it doesn't overlap.
 *
 * Selected state: indigo-500 border + indigo-50 bg + filled indigo radio.
 * Hover (unselected): indigo-200 border + slate-50 bg — deliberately three
 * shades lighter than selected so the small radio bead isn't the only
 * differentiator.
 */
export const ChoiceCard: FC<{
  icon: IconProp;
  title: string;
  description: string;
  billing?: string;
  selected: boolean;
  recommended?: boolean;
  innerRef?: React.Ref<HTMLButtonElement>;
  onClick: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
}> = ({
  icon,
  title,
  description,
  billing,
  selected,
  recommended,
  innerRef,
  onClick,
  onKeyDown,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    ref={innerRef}
    onClick={onClick}
    onKeyDown={onKeyDown}
    className={clsx(
      "relative cursor-pointer rounded-[10px] border-2 p-5 text-left transition-all",
      selected
        ? "border-indigo-500 bg-indigo-50"
        : "border-slate-200 bg-white hover:border-indigo-200 hover:bg-slate-50",
    )}
  >
    {/* .cc-recommended pill — top-left, opposite corner from .cc-radio */}
    {recommended && (
      <span
        aria-hidden="true"
        className="absolute top-[14px] left-[14px] rounded bg-indigo-100 px-[7px] py-0.5 text-[10px] font-bold tracking-[0.04em] text-indigo-500 uppercase"
      >
        Recommended
      </span>
    )}
    {/* .cc-radio — top-right */}
    <span
      aria-hidden="true"
      className={clsx(
        "absolute top-[14px] right-[14px] flex size-5 items-center justify-center rounded-full border-2",
        selected ? "border-indigo-500 bg-indigo-500" : "border-slate-300",
      )}
    >
      {selected && (
        <span className="size-1.5 rounded-full border-[2px] border-white" />
      )}
    </span>
    <div
      className={clsx(
        "text-[28px] leading-none text-indigo-500",
        recommended ? "mt-[22px] mb-3" : "mb-3",
      )}
      aria-hidden="true"
    >
      <FontAwesomeIcon icon={icon} />
    </div>
    <div className="mb-1 text-[15px] font-bold text-slate-900">{title}</div>
    <div className="text-[13px] leading-snug text-slate-500">{description}</div>
    {billing && (
      <div className="mt-2.5 text-xs font-medium text-indigo-500">
        {billing}
      </div>
    )}
  </button>
);
